import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { PANEL_TITLES, type PanelId } from '@trajectory/ui';

interface PanelErrorBoundaryProps {
  panelId: PanelId;
  children: ReactNode;
}

interface PanelErrorBoundaryState {
  error: Error | null;
}

/**
 * Isolates render failures to one dock panel. API and worker failures should
 * still use local states; this handles an unexpected component exception
 * without destroying other open work or the dock layout.
 */
export class PanelErrorBoundary extends Component<
  PanelErrorBoundaryProps,
  PanelErrorBoundaryState
> {
  override state: PanelErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): PanelErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `Trajectory GUI panel failure: ${this.props.panelId}`,
      error,
      info.componentStack,
    );
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-full items-center justify-center bg-surface-sunken p-4 text-center">
        <div
          role="alert"
          className="max-w-sm rounded-panel border border-danger/35 bg-surface-raised p-4 shadow-lg"
        >
          <AlertTriangle className="mx-auto h-5 w-5 text-danger" />
          <h2 className="mt-2 text-sm font-medium text-text">
            {PANEL_TITLES[this.props.panelId]} could not be rendered
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-muted">
            Other panels and saved documents are still available. Retry this panel; reload the
            application if the failure repeats.
          </p>
          <p className="mt-2 max-h-20 overflow-auto rounded-control bg-surface-sunken p-2 text-left text-[10px] text-danger">
            {this.state.error.message}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-3 inline-flex items-center gap-1.5 rounded-control bg-accent px-3 py-1.5 text-xs font-medium text-accent-contrast"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Retry panel
          </button>
        </div>
      </div>
    );
  }
}
